<script setup lang="ts">
import type { LawCommentaryNodeType } from '~/utils/solr/law-and-commentary'
import { getNsItemNumber, transformShortTitle } from '~/utils/solr/law-utils'

const props = defineProps<{
  nodeType?: LawCommentaryNodeType | null
  title?: string | null
  titleHtml?: string | null
  shortTitle?: string | null
  id?: string | null
}>()

const isStandardText = useIsStandardText()
const { textLabel } = useLawTextLabels()

const nsNumber = computed(() => isStandardText.value ? getNsItemNumber(props.id) : null)

const hTag = computed(() => {
  switch (props.nodeType) {
    case 'law':
      return { type: 'h2', class: 'title-m dark:text-white' }
    case 'chapter':
      return { type: 'h3', class: 'title-s dark:text-white' }
    case 'section':
      return { type: 'h4', class: 'title-xs dark:text-white' }
    default:
      // NS standards nest deeper than laws; give subsections (and similar deeper
      // nodes) a heading so the commentary button has something to anchor to.
      if (isStandardText.value && props.title) {
        return { type: 'h5', class: 'title-xs dark:text-white' }
      }
      return null
  }
})
</script>

<template>
  <div v-if="nodeType === 'law'">
    <component
      :is="hTag?.type" class="flex-1 break-words" :class="hTag?.class"
    >
      <span class="sr-only">{{ textLabel }} til </span>{{ transformShortTitle(shortTitle).title }}
    </component>
    <p class="title-xs !font-light dark:text-white">
      {{ title }}
    </p>
  </div>
  <component
    :is="hTag?.type"
    v-else
    class="flex-1"
    :class="hTag?.class"
  >
    <template v-if="nodeType !== 'section'">
      <span v-if="nsNumber" class="law-heading-number mr-2xs">{{ nsNumber }}</span>{{ title }}
    </template>
    <template v-else>
      <span v-if="nsNumber" class="law-heading-number mr-2xs">{{ nsNumber }}</span>
      <!-- Section title should include sup elements (footnotes) if any, we therefore use titleHtml -->
      <span v-if="titleHtml" class="[&_.legalArticleTitle]:ml-1" v-html="titleHtml" />
      <span v-else>{{ title }}</span>
    </template>
  </component>
</template>
